/* eslint-disable arrow-body-style */
import { BbFile, UploadService } from './../../../services/upload.service';
import { Subscription } from 'rxjs';
import { Mediaclip, MediaclipThumbnail, MediaclipThumbnailCrop } from './../../../interfaces/mediaclip';
import { SapiService } from './../../../services/sapi.service';
import { AssetService } from './../../../services/asset.service';
import { Component, Input, Output, EventEmitter, ViewChild, OnInit, OnChanges, OnDestroy, TemplateRef, SimpleChanges } from '@angular/core';
import { PublicationService } from 'app/services/publication.service';
import { Publication } from 'app/interfaces/publication';
import { DialogDefinition, ModalDefinition, OverlayService } from 'app/services/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { Project } from 'app/interfaces/project';
import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';
import { RadioListImage } from '../bb-radio-list/bb-radio-list.component';
import { ScreenshotImage } from '../bb-frame-select/bb-frame-select.component';
import { fitAspectRatioInAspectRatio, sameAspectRatio } from 'app/shared/utils/aspectRatio.util';
import { AspectRatioService, BBAspectRatio } from 'app/services/aspect-ratio.service';

export interface Thumbnail extends MediaclipThumbnail {
	data?: string;
	uploadIdentifier?: string;
	deleted?: boolean;
	indexBeforeDelete?: number;
	crop?: MediaclipThumbnailCrop;
	cropsetName: string;
	sameAspectAsVideo?: boolean;
}
export interface ConfigCropset {
	name: string;
	default: boolean;
	aspectratio?: number;
}
interface ThumbnailSet {
	[id: string]: Thumbnail[];
}
@Component({
	selector: 'thumbnail-selector',
	templateUrl: './thumbnail-selector.component.html',
	styleUrls: ['./thumbnail-selector.component.scss']
})

export class ThumbnailSelectorComponent implements OnInit, OnChanges, OnDestroy {
	@Input() asset: string;
	@Input() assetTitle?: string;
	@Input() thumbnails: Thumbnail[] = [];
	@Input() entity: Mediaclip | Project;

	@Input() isProject = false;
	@Input() isCreative = false;

	@Output() onChange: EventEmitter<Thumbnail[]> = new EventEmitter<Thumbnail[]>();

	@ViewChild('screenshotVideoTemplate') screenshotVideoTemplate: TemplateRef<any>;
	@ViewChild('screenshotImageTemplate') screenshotImageTemplate: TemplateRef<any>;
	@ViewChild('cropsetTemplate') cropsetTemplate: TemplateRef<HTMLDivElement>;

	screenshotModal: ModalDefinition;
	cropsetModal: ModalDefinition;
	thumbnailWidth = '960';
	image: ScreenshotImage;

	sessionToken: string;
	thumbnailPrefix: string;
	defaultMediaAssetPath: string;
	internalThumbnails: ThumbnailSet = {}; // for display

	cropsetConfig: BBAspectRatio[] = [];
	cropsetOptions: RadioListImage[] = [];
	selectedCropset: string;
	userAddedCropsets: string[] = [];
	cropsetHasMain: Partial<Record<string, boolean>> = {};

	clipAspectRatioName: string;
	generateDefaultsNewSet = true;
	scrollCropsetInView: string;

	private subscriptions = new Subscription();

	constructor(
		private uploadService: UploadService,
		private assetService: AssetService,
		private sapiService: SapiService,
		private publicationService: PublicationService,
		private translate: TranslateService,
		private overlayService: OverlayService,
		private aspectRatioService: AspectRatioService
	) { }

	ngOnInit() {
		this.aspectRatioService.aspectRatiosObs.subscribe(sets => {
			this.cropsetConfig = sets;
			this.setCropsetOptions();
		});

		this.sessionToken = this.sapiService.getSessionToken();

		this.setInternalThumbnails();

		this.subscriptions.add(
			this.publicationService.getData().subscribe((publicationData: Publication) => {
				if (!publicationData) {
					return;
				}
				this.defaultMediaAssetPath = publicationData.defaultMediaAssetPath;
			})
		);

		this.subscriptions.add(
			this.uploadService.completedObservable.subscribe((file: BbFile) => {
				this.onThumbnailUploaded(file);
			})
		);

		this.determineClipAspectRatio();
	}

	ngOnChanges(changesObj: SimpleChanges) {
		// eslint-disable-next-line guard-for-in
		for (const key in changesObj) {
			if (key === 'entity') {
				this.determineClipAspectRatio();
				// entity save/cancel: remove empty cropsets from interface
				Object.keys(this.internalThumbnails).forEach(set => {
					if (this.internalThumbnails[set].length === 0) {
						delete this.internalThumbnails[set];
					}
				});
				this.userAddedCropsets = [];
				this.setCropsetOptions();
				return;
			}
			if (key === 'thumbnails') {
				if (changesObj.thumbnails.firstChange) {
					return;
				}
				// if e.g. deleted and saved the parent entity, thumbnails will have updated
				this.setInternalThumbnails();
			}
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	private onThumbnailUploaded(file: BbFile) {
		const cropset: string = file.customData?.cropset;
		const width = file.thumbData?.width?.toString();
		const height = file.thumbData?.height?.toString();

		if (!cropset || !file.thumbData?.data) {
			return; // should not occur
		}

		// supply crop with aspect ratio so bb-frame-select can load the correct one
		const crop: MediaclipThumbnailCrop = { cropAspectRatio: this.cropsetConfig.find(c => c.name === cropset)?.aspectratio, x: null, y: null, width: null, height: null, main: true };

		this.cropImage({ data: file.thumbData.data, uploadIdentifier: file.uploadIdentifier, width, height, crop, cropsetName: cropset });
	}

	onScreenshotClick() {
		this.screenshotModal = {
			icon: 'cut',
			title: this.translate.instant('SELECT_THUMBNAIL_FROM_VIDEO'),
			allowClose: () => true,
			component: {
				name: 'custom-content',
				templateRef: this.screenshotVideoTemplate
			}
		};

		this.overlayService.createModal(this.screenshotModal);
	}

	onScreenshotCancel() {
		this.overlayService.removeModal(this.screenshotModal);
	}

	cropImage(thumbnail: Thumbnail): void {
		// image is either with data and uploadIdentifier (just uploaded) or with src and srcPrefix (already in backend)
		this.image = {
			width: parseInt(thumbnail.width, 10),
			height: parseInt(thumbnail.height, 10),
			src: thumbnail.src,
			srcPrefix: thumbnail.srcPrefix,
			crop: thumbnail.crop,
			data: thumbnail.data,
			uploadIdentifier: thumbnail.uploadIdentifier,
			cropsetName: thumbnail.cropsetName
		};

		this.screenshotModal = {
			icon: 'cut',
			title: this.translate.instant('SELECT_THUMBNAIL_FROM_IMAGE'),
			allowClose: () => true,
			component: {
				name: 'custom-content',
				templateRef: this.screenshotImageTemplate
			}
		};

		this.overlayService.createModal(this.screenshotModal);
	}

	filterUploads(file: BbFile): boolean {
		return (file.type.indexOf('image/') > -1);
	}

	private setInternalThumbnails() {
		// convert to crop set groups
		const entitythumbnails = bbDeepCopy(this.thumbnails);
		const thumbnailSets: ThumbnailSet = {};
		this.cropsetHasMain = {};

		entitythumbnails.forEach((thumb: Thumbnail) => {
			if (!thumb.srcPrefix) {
				thumb.srcPrefix = this.setThumbnailPrefix(thumb.width, thumb.height);
			}


			Object.keys(thumb.crops).forEach((set: string) => {
				if (!thumbnailSets[set]) {
					// make sure this thumbnailset is still active in this publication
					if (!this.cropsetConfig.find(config => config.name === set)) {
						return;
					}
					thumbnailSets[set] = [];
				};

				const t: Thumbnail = { ...thumb };
				t.crop = thumb.crops[set];
				t.cropsetName = set;
				delete t.crops;

				if (set !== 'default') {
					t.crop.cropAspectRatio = this.cropsetConfig.find(config => config.name === set).aspectratio;
				} else {
					// determine aspect ratio of crop for default (can be free transform)
					// some thumbnails don't have width/height set
					if (t.src && (!t.width || t.width === '0' || !t.height || t.height === '0')) {
						const image = new Image();
						image.src = t.srcPrefix + t.src + '?sesstoken=' + this.sessionToken;
						image.onload = () => {
							// note: updated async by ref
							t.width = image.width.toString();
							t.height = image.height.toString();
							t.crop.cropAspectRatio = (t.crop.width * image.width) / (t.crop.height * image.height);
						};
					} else {
						t.crop.cropAspectRatio = (t.crop.width * parseInt(t.width, 10)) / (t.crop.height * parseInt(t.height, 10));
					}
				}

				if (t.crop.main) {
					this.cropsetHasMain[set] = true;
				}
				thumbnailSets[set].push(t);
			});
		});

		this.userAddedCropsets.forEach(set => {
			if (!thumbnailSets[set]) {
				thumbnailSets[set] = [];
			}
		});

		this.internalThumbnails = thumbnailSets;
		this.setCropsetOptions();

		if (this.scrollCropsetInView) {
			this.scrollToLatestCropset();
		}
	}

	private deselectAll(cropsetName: string) {
		// first deselect all thumbnails in the ui version of our array
		if (this.internalThumbnails[cropsetName]) {
			this.internalThumbnails[cropsetName].forEach((thumb) => {
				thumb.crop.main = false;
			});
		}

		// and also in the "external" thumbnail array
		this.thumbnails.forEach((thumbnail, index) => {
			if (thumbnail.crops[cropsetName]) {
				thumbnail.crops[cropsetName].main = false;
			}
		});
	}

	// Called for screenshot of video (media clip) and when cropping uploaded/existing image
	// Example portrait taken of a 16:9 screenshot crop { portrait: { height: 0.8889, main: true, width: 0.2813, x: 0.1, y: 0.1 } }
	onCropSave(screenshot: Partial<Thumbnail>, useAsMain = true) {
		const defaultCrop: MediaclipThumbnailCrop = { height: 1, main: true, width: 1, x: 0, y: 0 };

		if (useAsMain) {
			this.deselectAll(screenshot.cropsetName);
			screenshot.crop.main = true;
		}

		let currentIndex = this.thumbnails.findIndex(th => th.src && th.src === screenshot.src);
		// if not found: check on uploadIdentifier for just uploaded img that you cropped before saving entity
		if (currentIndex === -1 && screenshot.uploadIdentifier) {
			currentIndex = this.thumbnails.findIndex(th => th.uploadIdentifier && th.uploadIdentifier === screenshot.uploadIdentifier);
		}

		let updatedThumbnail: Thumbnail;
		if (screenshot.data) {
			updatedThumbnail = {
				data: screenshot.data, uploadIdentifier: screenshot.uploadIdentifier, cropsetName: screenshot.cropsetName,
				width: screenshot.width, height: screenshot.height, crops: { [screenshot.cropsetName]: screenshot.crop ?? defaultCrop }
			};
		} else {
			updatedThumbnail = {
				src: screenshot.src, srcPrefix: screenshot.srcPrefix, cropsetName: screenshot.cropsetName,
				width: screenshot.width, height: screenshot.height, crops: { [screenshot.cropsetName]: screenshot.crop ?? defaultCrop }
			};
		}

		if (currentIndex !== -1) {
			// update cropset, or add new cropset
			this.thumbnails[currentIndex].crops[screenshot.cropsetName] = screenshot.crop ?? defaultCrop;
		} else {
			// new thumbnail
			this.thumbnails.unshift(updatedThumbnail);
		}

		this.scrollCropsetInView = screenshot.cropsetName;
		this.setInternalThumbnails();

		this.overlayService.removeModal(this.screenshotModal);
		this.valueChanged();
	}

	selectThumbnail(thumbnailToSelect: Thumbnail) {
		if (thumbnailToSelect.deleted) {
			return;
		}

		this.deselectAll(thumbnailToSelect.cropsetName);

		// select thumb in ui version of our thumbnail array (internalThumbnails)
		thumbnailToSelect.crop.main = true;

		// and set main in the actual data
		// check data for thumbnails selected from video, as they have no src yet
		this.thumbnails.forEach((thumbnail, index) => {
			if ((thumbnail.src && thumbnail.src === thumbnailToSelect.src)
				|| (thumbnail.uploadIdentifier && thumbnail.uploadIdentifier === thumbnailToSelect.uploadIdentifier)
				|| (thumbnail.data && thumbnail.data === thumbnailToSelect.data)
			) {
				thumbnail.crops[thumbnailToSelect.cropsetName].main = true;
				this.cropsetHasMain[thumbnailToSelect.cropsetName] = true;
			}
		});
		this.valueChanged();
	}

	valueChanged() {
		// we use new data structure with crops, for backwards comp we need to remove the main in thumbnail so the main in crop is used for saving
		// dirty in forEach so ngOnChanges is not triggered
		this.thumbnails.forEach(th => {
			th.main = false;
		});
		this.onChange.emit(this.thumbnails);
	}

	downloadThumbnail(thumbnail: Thumbnail, index: number): void {
		let filename = '';
		if (this.assetTitle) {
			filename += `${this.assetTitle.split('.').join('_')} `;
		}
		filename += `thumbnail ${index + 1}`;

		// data URI base64 encoded, not saved yet on s3
		if (thumbnail.data) {
			const linkSource = thumbnail.data;
			const downloadLink = document.createElement('a');
			downloadLink.href = linkSource;
			downloadLink.download = filename;
			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);
			return;
		}

		// direct download from cloudfront for original/high quality, bypassing resizer
		// could not download with basic html/js since it is hosted on a differen domain
		const extension = `.${thumbnail.src.split('.').pop()}`;
		this.sapiService
			.downloadFromUrl(`${this.defaultMediaAssetPath}${thumbnail.src}`, filename, 0, extension)
			.subscribe();
	}

	addCropSet(): void {
		this.cropsetModal = {
			icon: 'camera',
			title: this.translate.instant('ADD_THUMBNAIL_SET'),
			allowClose: () => true,
			component: {
				name: 'custom-content',
				templateRef: this.cropsetTemplate
			}
		};

		this.overlayService.createModal(this.cropsetModal);
	}

	onCancelAddCropset(): void {
		this.overlayService.removeModal(this.cropsetModal);
	}

	onSaveAddCropset(): void {
		const selected = this.selectedCropset;

		if (!selected) {
			// no cropset selected (or undefined if already added all)
			this.overlayService.removeModal(this.cropsetModal);
			return;
		}

		if (!this.userAddedCropsets.includes(selected)) {
			this.userAddedCropsets.push(selected);
			this.valueChanged();
		}

		if (this.generateDefaultsNewSet) {
			this.generateDefaultThumbnailsForSet(selected);
		}

		if (!this.internalThumbnails[selected]) {
			this.internalThumbnails[selected] = [];
			this.setCropsetOptions();
		}

		this.overlayService.removeModal(this.cropsetModal);
	}

	deleteCropset(cropset: string) {
		const translation = this.translate.instant('THUMBNAIL_SET').toLowerCase();

		const dialog: DialogDefinition = {
			icon: 'trash',
			title: this.translate.instant('DELETING_ENTITY', { entity: translation, id: this.translate.instant(cropset.toUpperCase()) }),
			onConfirm: () => {
				delete this.internalThumbnails[cropset];
				this.thumbnails.forEach((thumb) => {
					delete thumb.crops[cropset];
				});
				this.thumbnails = this.thumbnails.filter(th => Object.keys(th.crops).length > 0);
				this.userAddedCropsets = this.userAddedCropsets.filter(c => c !== cropset);

				this.valueChanged();
			},
			contents: this.translate.instant('DELETE_ENTITY_CONFIRM', { entity: translation })
		};

		this.overlayService.createDialog(dialog);
	}

	deleteThumbnail(thumbnailToDelete: Thumbnail) {
		if (thumbnailToDelete.crop.main) {
			this.cropsetHasMain[thumbnailToDelete.cropsetName] = false;
			thumbnailToDelete.crop.main = false;
		}

		thumbnailToDelete.deleted = true;

		this.thumbnails.forEach((thumbnail, index) => {
			if (
				(thumbnailToDelete.src && thumbnail.src === thumbnailToDelete.src) ||
				(thumbnailToDelete.uploadIdentifier && thumbnail.uploadIdentifier === thumbnailToDelete.uploadIdentifier) ||
				(thumbnailToDelete.data && thumbnail.data === thumbnailToDelete.data)
			) {
				// remember original location of this thumbnail in the array
				thumbnailToDelete.indexBeforeDelete = index;
				// delete from crops in data
				delete this.thumbnails[index].crops[thumbnailToDelete.cropsetName];

				// check if all crops deleted for this thumbnail
				if (Object.keys(this.thumbnails[thumbnailToDelete.indexBeforeDelete].crops).length === 0) {
					this.thumbnails.splice(thumbnailToDelete.indexBeforeDelete, 1);
				}
			}
		});

		this.valueChanged();
	}

	undoDeleteThumbnail(undoThumbnail: Thumbnail) {
		const restoreIndex = undoThumbnail.indexBeforeDelete;
		const crop = undoThumbnail.crop;

		// remove ui-stuff from thumbnail
		delete (undoThumbnail.deleted);
		delete (undoThumbnail.indexBeforeDelete);

		// re-add to thumbnail array at original location
		// do we have the thumbnail still?
		const thumbIndex = this.thumbnails.findIndex(thumb => {
			return (undoThumbnail.src && thumb.src === undoThumbnail.src)
				|| (undoThumbnail.uploadIdentifier && thumb.uploadIdentifier === undoThumbnail.uploadIdentifier)
				|| (undoThumbnail.data && thumb.data === undoThumbnail.data);
		});

		if (thumbIndex === -1) {
			// thumbnail not there, restore
			const thumbnailRestore: Thumbnail = { ...undoThumbnail, ...{ crops: {} } };
			delete thumbnailRestore.crop;
			this.thumbnails.splice(restoreIndex, 0, thumbnailRestore);
		}

		// restoreIndex might not exist if we deleted multiple
		const newIndex = this.thumbnails.findIndex(thumb => {
			return (undoThumbnail.src && thumb.src === undoThumbnail.src)
				|| (undoThumbnail.uploadIdentifier && thumb.uploadIdentifier === undoThumbnail.uploadIdentifier)
				|| (undoThumbnail.data && thumb.data === undoThumbnail.data);
		});
		this.thumbnails[newIndex].crops[undoThumbnail.cropsetName] = crop;

		// if no other main, make this one main
		if (!this.internalThumbnails[undoThumbnail.cropsetName].some(th => th.crop.main)) {
			this.selectThumbnail(undoThumbnail);
		}

		this.valueChanged();
	}

	// check dimensions thumbnail and scale down to width this.thumbnailWidth for smaller filesize, default to 16:9 ratio
	private setThumbnailPrefix(w?: string, h?: string): string {
		const width: number = parseInt(this.thumbnailWidth, 10);
		let height: string | number = parseInt(this.thumbnailWidth, 10) * (9 / 16);

		if (w && h && w !== '0' && h !== '0') {
			const widthRatio = parseInt(w, 10) / width;
			height = Math.floor(parseInt(h, 10) / widthRatio);
		}

		const prefix = this.assetService.getImageResizerBaseUrl(width, height);
		return prefix;
	}

	// Set options for ADD THUMBNAIL SET and disabling those already present
	private setCropsetOptions() {
		const inUse = Object.keys(this.internalThumbnails);

		this.cropsetOptions = [];

		this.cropsetConfig
			.filter(r => !r.default)
			.forEach(set => {
				this.cropsetOptions.push({
					value: set.name,
					label: set.label + ' ' + set.value,
					bgAspectRatio: set.aspectratio,
					disabled: inUse.includes(set.name)
				});
			});

		this.selectedCropset = this.cropsetOptions.find(set => !inUse.includes(set.value))?.value;
	}

	// for mediaclips, do not allow remove thumbnail set with aspect ratio equal to video
	// note: project has no width/height so can remove all
	private determineClipAspectRatio(): void {
		if (this.isProject || !this.entity) {
			return;
		}

		const w: string = (this.entity as any).width;
		const h: string = (this.entity as any).height;
		let clipAspectRatioName: string = null;

		if (h && w && w !== '0' && h !== '0') {
			const videoAspectRatio = parseInt(w, 10) / parseInt(h, 10);
			this.cropsetConfig.forEach(cr => {
				if (sameAspectRatio(cr.aspectratio, videoAspectRatio)) {
					// prioritize standard cropsets
					if (!cr.custom) {
						clipAspectRatioName = cr.name;
					} else if (cr.custom && !clipAspectRatioName) {
						clipAspectRatioName = cr.name;
					}
				}
			});
		}
		this.clipAspectRatioName = clipAspectRatioName;
	}

	private scrollToLatestCropset(): void {
		setTimeout(() => {
			const header = document.getElementById(`cropset-${this.scrollCropsetInView}`);
			if (header) {
				header.scrollIntoView({ block: 'nearest', inline: 'nearest' });
			}
			this.scrollCropsetInView = null;
		});
	}

	// Currently only for mediaclips since for those we know the clipAspectRatioName (because it has width and height)
	private generateDefaultThumbnailsForSet(newCropsetName: string): void {
		if (!this.clipAspectRatioName || !newCropsetName) {
			return;
		}

		// get mediaclip default thumbnails (those matching aspect ratio)
		const defaultCrops = this.internalThumbnails[this.clipAspectRatioName];
		const newCropAspectRatio = this.aspectRatioService.getAspectRatioByName(newCropsetName)?.aspectratio;

		if (!defaultCrops?.length || !newCropAspectRatio) {
			return;
		}

		const newCrops = bbDeepCopy(defaultCrops).map((cropset) => {
			// .. can also upload e.g. portrait original in landscape and crop that: therefor use height/width of img instead of crop aspect ratio
			// these images always have width/height otherwise they would not be in the clip default cropset
			const cropDimensions = fitAspectRatioInAspectRatio(newCropAspectRatio, (parseInt(cropset.width, 10) / parseInt(cropset.height, 10)));
			cropset.crop = {
				x: cropDimensions.leftRatio,
				y: cropDimensions.topRatio,
				width: cropDimensions.widthRatio,
				height: cropDimensions.heightRatio,
				main: cropset.crop.main,
				cropAspectRatio: newCropAspectRatio
			};
			cropset.cropsetName = newCropsetName;
			return cropset;
		});

		newCrops.forEach(thumbnail => {
			this.onCropSave(thumbnail, false);
		});
	}
}
